import { observer } from 'mobx-react';
import { useMemo } from 'react';

import { ProductDetailsSectionTranslation } from '../../../../../app/localization/SiteTranslation';
import { AnalyticsAttributes } from '../../../../../app/shared/analytics/AnalyticsAttributes';
import AssetsDownloader from '../../../../../app/shared/components/AssetsDownloader/AssetsDownloader';
import { AssetsDownloaderState } from '../../../../../app/shared/components/AssetsDownloader/AssetsDownloaderState';
import ProductSpecification from '../../../../shared/components/ProductSpecification/ProductSpecification';
import { TabbedInterfaceItem } from '../../../../shared/components/TabbedInterface/TabbedInterface';
import Tabs from '../../../../shared/components/Tabs/Tabs';
import useComponentInstanceCreator from '../../../../shared/hooks/useComponentInstanceCreator';
import ProductVariationsState from '../ProductVariations/ProductVariationsState';
import { ProductCareSectionProps } from './ProductCare/ProductCareSection/ProductCareSection';
import css from './ProductDetailsSection.css';

const ANALYTICS_PRODUCT_DETAILS_TAB_ID = 'Product details tab';

interface ProductDetailsSection {
  assetsDownloaderState: AssetsDownloaderState;
  guideUrl: string;
  id: string;
  maintenance: ProductCareSectionProps[];
  productName: string;
  specification: string;
  translation: ProductDetailsSectionTranslation;
  variationsState: ProductVariationsState;
}

const ProductDetailsSection = ({
  assetsDownloaderState,
  guideUrl,
  id,
  maintenance,
  productName,
  specification,
  translation,
  variationsState,
}: ProductDetailsSection) => {
  /*
   * The AssetsDownloader component is recreated when the assetsDownloaderInstanceKey changes in order to close all expanded Accordion components inside it.
   * An accordion component uses a fully uncontrolled ExpandablePanel component so it's not possible to change its internal state outside a component.
   * */
  const [assetsDownloaderInstanceKey, resetAssetsDownloaderInstanceKey] = useComponentInstanceCreator();

  const tabs = useMemo<TabbedInterfaceItem[]>(() => {
    const result: TabbedInterfaceItem[] = [];

    if (specification) {
      result.push({
        name: translation.specificationSectionTitle,
        content: <ProductSpecification description={specification} />,
        ...AnalyticsAttributes.get({
          id: ANALYTICS_PRODUCT_DETAILS_TAB_ID,
          label: translation.specificationSectionTitle,
          product: productName,
          'product-code': variationsState.selectedConfigurationCode,
        }),
      });
    }

    // TODO: we don't have content yet. Maybe this section can be unified with product specification?
    // result.push(
    //   {
    //     name: translation.maintenanceSectionTitle,
    //     content: <ProductCare guideUrl={guideUrl} sections={maintenance} translation={translation.productCare} />,
    //   },
    // );

    if (assetsDownloaderState.categories.any()) {
      result.push({
        name: translation.downloadsSectionTitle,
        content: (
          <AssetsDownloader
            state={assetsDownloaderState}
            translation={translation.assetsDownloader}
            key={assetsDownloaderInstanceKey}
            {...AnalyticsAttributes.get({
              product: productName,
              'product-code': variationsState.selectedConfigurationCode,
            })}
          />
        ),
        ...AnalyticsAttributes.get({
          id: ANALYTICS_PRODUCT_DETAILS_TAB_ID,
          label: translation.downloadsSectionTitle,
          product: productName,
          'product-code': variationsState.selectedConfigurationCode,
        }),
        onClose: resetAssetsDownloaderInstanceKey,
      });
    }

    return result;
  }, [
    assetsDownloaderInstanceKey,
    assetsDownloaderState,
    guideUrl,
    maintenance,
    resetAssetsDownloaderInstanceKey,
    specification,
    translation,
  ]);

  return (
    <div className={css.container}>
      <Tabs id={`product-details-${id}`} tabs={tabs} />
    </div>
  );
};

export default observer(ProductDetailsSection);
